import { BellRing, BookOpen, Brain, CalendarDays, ChevronRight, Cloud, Download, ImagePlus, LogOut, Pencil, Repeat2, Ruler, Save, Scale, ShieldCheck, Smartphone, TrendingUp } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useAuth } from '../../auth/AuthGate'
import { Modal } from '../../components/Modal'
import { NumberControl } from '../../components/NumberControl'
import { athleteContext } from '../../data/athleteContext'
import { useStrata } from '../../data/StrataProvider'
import { dietDayNumber, localDateKey } from '../../domain/calendar'
import { overloadCycle } from '../../domain/overload'
import type { BodyMeasurement } from '../../domain/types'
import { displayMass, formatMass, massUnit, storeMass } from '../../domain/units'
import { ExerciseLibrary } from '../library/ExerciseLibrary'
import { AthleteSetupForm } from './AthleteSetupForm'

export function ProfilePage() {
  const { user, logout } = useAuth()
  const { state, syncStatus, addMeasurement, updatePreferences, saveAthleteProfile } = useStrata()
  const [library, setLibrary] = useState(false)
  const [weightEntry, setWeightEntry] = useState(false)
  const [profileEntry, setProfileEntry] = useState(false)
  const [installHelp, setInstallHelp] = useState(false)
  const measurements = [...state.measurements].sort((a, b) => b.date.localeCompare(a.date))
  const [weight, setWeight] = useState(() => measurements[0]?.weight ?? 75)
  const photos = measurements.filter((item) => item.photo)
  const prCount = state.sessions.reduce((sum, session) => sum + session.prCount, 0)
  const day = dietDayNumber(state.profile.dietStartedOn, localDateKey())
  const targets = state.profile.nutritionTargets
  const overload = overloadCycle(state.profile, state.preferences)

  const saveWeight = () => {
    addMeasurement({ id: crypto.randomUUID(), date: new Date().toISOString(), weight })
    setWeightEntry(false)
  }

  const addPhoto = (file?: File) => {
    if (!file) return
    addMeasurement({ id: crypto.randomUUID(), date: new Date().toISOString(), weight: measurements[0]?.weight ?? weight, photo: file })
  }

  return (
    <div className="page profile-page">
      <header className="profile-header">
        <div className="avatar">J</div>
        <div><h1>{state.profile.name}</h1><p>{state.profile.age} años · {state.profile.heightCm} cm · {prCount} PR</p></div>
        <button className="icon-button profile-edit" onClick={() => setProfileEntry(true)} aria-label="Editar perfil"><Pencil size={18} /></button>
      </header>

      <section className="profile-section">
        <span className="eyebrow">DEFINICIÓN</span>
        <div className="diet-profile-summary">
          <div><span>DÍA</span><strong>{day || '—'}</strong></div>
          <div><span>SEMANA</span><strong>{day ? Math.ceil(day / 7) : '—'}</strong></div>
          <div><span>COMPLETADOS</span><strong>{state.dietCheckIns.length}</strong></div>
        </div>
        <div className="macro-profile-line"><span>{targets.calories} kcal</span><span>{targets.protein}P</span><span>{targets.carbohydrates}C</span><span>{targets.fat}G</span></div>
        <button className="profile-row" onClick={() => setProfileEntry(true)}><CalendarDays /><span>Datos y macros actuales</span><ChevronRight /></button>
      </section>

      <section className="profile-section">
        <span className="eyebrow">CUERPO</span>
        <div className="body-weight"><div><strong>{measurements[0] ? formatMass(measurements[0].weight, state.preferences.unit) : '—'}</strong><span>{measurements[0] ? shortDate(measurements[0].date) : 'Sin registrar'}</span></div><button className="text-action" onClick={() => setWeightEntry(true)}>Añadir peso</button></div>
        {photos.length > 0 && <div className="photo-strip">{photos.map((item) => <PhotoTile key={item.id} measurement={item} />)}</div>}
        <label className="upload-action"><ImagePlus size={19} />Añadir foto de progreso<input type="file" accept="image/*" onChange={(event) => addPhoto(event.target.files?.[0])} /></label>
      </section>

      <section className="profile-section">
        <span className="eyebrow">ENTRENAMIENTO</span>
        <button className="profile-row" onClick={() => setLibrary(true)}><BookOpen /><span>Biblioteca de ejercicios</span><em>{state.exercises.length}</em><ChevronRight /></button>
        <div className="profile-row static"><Brain /><span>Contexto para IA</span><em>v{athleteContext.version} listo</em></div>
      </section>

      <section className="profile-section">
        <span className="eyebrow">PREFERENCIAS</span>
        <div className="profile-row"><Ruler /><span>Unidades</span><select value={state.preferences.unit} onChange={(event) => updatePreferences({ ...state.preferences, unit: event.target.value as 'metric' | 'imperial' })}><option value="metric">Métrico</option><option value="imperial">Imperial</option></select></div>
        <label className="profile-row"><BellRing /><span>Aviso al acabar descanso</span><input type="checkbox" checked={state.preferences.restSound} onChange={(event) => updatePreferences({ ...state.preferences, restSound: event.target.checked })} /></label>
      </section>

      <section className="profile-section">
        <span className="eyebrow">PROGRESIÓN</span>
        <div className="profile-row static"><Save /><span>Guardar pesos y series</span><em>Al instante</em></div>
        <div className="profile-row"><Repeat2 /><span>Recordar última carga</span><select aria-label="Durante cuánto tiempo recordar cargas" value={state.preferences.loadMemoryWeeks} onChange={(event) => updatePreferences({ ...state.preferences, loadMemoryWeeks: Number(event.target.value) })}><option value="0">Siempre</option><option value="4">4 semanas</option><option value="8">8 semanas</option><option value="12">12 semanas</option><option value="26">6 meses</option></select></div>
        <div className="profile-row"><TrendingUp /><span>Sobrecarga cada</span><select aria-label="Frecuencia de sobrecarga progresiva" value={state.preferences.overloadEveryWeeks} onChange={(event) => updatePreferences({ ...state.preferences, overloadEveryWeeks: Number(event.target.value) })}>{[1, 2, 3, 4, 6, 8].map((weeks) => <option value={weeks} key={weeks}>{weeks} {weeks === 1 ? 'semana' : 'semanas'}</option>)}</select></div>
        <label className="profile-row cycle-date"><CalendarDays /><span>Inicio del ciclo</span><input type="date" max={localDateKey()} value={state.preferences.overloadStartedOn} onChange={(event) => updatePreferences({ ...state.preferences, overloadStartedOn: event.target.value })} /></label>
        <div className={`cycle-summary ${overload.due ? 'due' : ''}`}><strong>{overload.configured ? `Semana ${overload.programWeek} · ciclo ${overload.cycleWeek}/${overload.every}` : 'Elige una fecha de inicio'}</strong><span>{overload.due ? 'Toca sobrecarga progresiva' : overload.configured ? `Próxima carga en ${overload.weeksUntil} ${overload.weeksUntil === 1 ? 'semana' : 'semanas'}` : 'Así sabremos cuándo mostrar el aviso'}</span></div>
      </section>

      <section className="profile-section">
        <span className="eyebrow">ESTA APP</span>
        <button className="profile-row" onClick={() => setInstallHelp(true)}><Download /><span>Instalar en iPhone</span><em>Gratis</em><ChevronRight /></button>
        <div className="profile-row static"><ShieldCheck /><span>Acceso privado</span><em>Protegido</em></div>
        <div className="profile-row static"><Smartphone /><span>Datos en este dispositivo</span><em>Activo</em></div>
        <div className="profile-row static"><Cloud /><span>Sincronización de cuenta</span><em>{syncLabel(syncStatus)}</em></div>
        <div className="profile-row static"><ShieldCheck /><span>Cuenta</span><em>{user.email}</em></div>
        <button className="profile-row danger-row" onClick={() => { if (window.confirm('¿Cerrar sesión en este dispositivo? Tus datos seguirán guardados en tu cuenta.')) void logout() }}><LogOut /><span>Cerrar sesión</span><ChevronRight /></button>
        <p className="support-copy">Perfil, macros, pesos, adherencia y sesiones se sincronizan con tu cuenta. Las fotos y videos personales permanecen únicamente en este dispositivo.</p>
      </section>

      {library && <ExerciseLibrary onClose={() => setLibrary(false)} />}
      {weightEntry && (
        <Modal title="Peso corporal" onClose={() => setWeightEntry(false)}>
          <div className="weight-entry"><NumberControl label="Hoy" unit={massUnit(state.preferences.unit)} value={displayMass(weight, state.preferences.unit)} step={state.preferences.unit === 'imperial' ? .2 : .1} decimals={2} onChange={(value) => setWeight(storeMass(value, state.preferences.unit))} /><button className="primary-button" onClick={saveWeight}><Scale size={18} />Guardar</button></div>
        </Modal>
      )}
      {profileEntry && (
        <Modal title="Tu base actual" onClose={() => setProfileEntry(false)}>
          <AthleteSetupForm
            initialProfile={state.profile}
            initialWeight={measurements[0]?.weight ?? weight}
            submitLabel="Guardar cambios"
            unit={state.preferences.unit}
            onSave={(profile, currentWeight) => { saveAthleteProfile(profile, currentWeight); setProfileEntry(false) }}
          />
        </Modal>
      )}
      {installHelp && (
        <Modal title="Instalar Strata" onClose={() => setInstallHelp(false)}>
          <ol className="install-steps"><li>Abre <strong>training.ownhome.me</strong> en Safari e inicia sesión.</li><li>Toca el botón <strong>Compartir</strong>.</li><li>Selecciona <strong>Añadir a pantalla de inicio</strong>.</li><li>Activa <strong>Abrir como app web</strong> y toca Añadir.</li></ol>
          <p className="support-copy">Después se abrirá a pantalla completa desde su propio icono, igual que una app.</p>
        </Modal>
      )}
    </div>
  )
}

function PhotoTile({ measurement }: { measurement: BodyMeasurement }) {
  const [source, setSource] = useState('')
  useEffect(() => {
    if (!measurement.photo) return
    const url = URL.createObjectURL(measurement.photo)
    setSource(url)
    return () => URL.revokeObjectURL(url)
  }, [measurement.photo])
  return source ? <figure><img src={source} alt={`Progreso del ${shortDate(measurement.date)}`} /><figcaption>{shortDate(measurement.date)}</figcaption></figure> : null
}

const shortDate = (value: string) => new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short' }).format(new Date(value))
const syncLabel = (status: 'syncing' | 'synced' | 'offline') => status === 'synced' ? 'Al día' : status === 'syncing' ? 'Guardando…' : 'Solo local'
